import { HEALTH_DISPLAY } from '@/constants';
import { getHealthFactor } from '@/utils/getHealthFactor';
import { getDisplayedHealthPercent } from '@/utils/health-display';

export interface VaultData {
  tag: string | undefined;
  healthValue: number;
  maxHealth: number;
  unitInVault: number;
  btcInVault: number;
  availableBtc: number;
  lockedBtc: number;
}

export const MASK_STYLE = {
  maskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 25%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0) 100%)',
  WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 25%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0) 100%)',
};

export const minBtcInVault = 0.0001; // 10000 satoshis

// `tag` is resolved from the `layout.statusBox.defaultTag` message at the rendering component.
export const loadingVaultData: VaultData = {
  tag: undefined,
  healthValue: Infinity,
  maxHealth: Infinity,
  unitInVault: 0,
  btcInVault: minBtcInVault,
  availableBtc: minBtcInVault,
  lockedBtc: 0,
};

export const mockData: VaultData = {
  tag: undefined,
  healthValue: 650,
  maxHealth: 1000,
  unitInVault: 100000,
  btcInVault: 5.8,
  availableBtc: 4.35,
  lockedBtc: 1.45,
};

export const getIsFullyHealthy = (unitInVault: number | undefined, btcInVault: number | undefined): boolean => {
  if (unitInVault === undefined || btcInVault === undefined) {
    return false;
  }

  return unitInVault === 0 && btcInVault > minBtcInVault;
};

export const getIsInfinity = (value: number): boolean => {
  return value === Number.POSITIVE_INFINITY;
};

// almost the same logic as getHealthFactor
export const getProgressBarColorClass = (isLoading: boolean, value: number, unitInVault: number, btcInVault: number): string => {
  value = getDisplayedHealthPercent(value);

  if (isLoading) {
    return 'bg-border-primary';
  }

  const isInfinity = getIsInfinity(value);

  if (isInfinity && getIsFullyHealthy(unitInVault, btcInVault)) {
    return 'bg-green-100';
  }

  if (Number.isNaN(value)) {
    return 'bg-text-primary';
  }

  if (value <= HEALTH_DISPLAY.RISKY_MAX) {
    return 'bg-red-100';
  }

  if (value >= HEALTH_DISPLAY.HEALTHY_MIN) {
    return 'bg-green-100';
  }

  return 'bg-vault-caution';
};

export const getBackgroundColorClass = (isLoading: boolean, value: number): string => {
  value = getDisplayedHealthPercent(value);

  if (isLoading) {
    return 'bg-border-primary';
  }

  if (getIsInfinity(value) || Number.isNaN(value)) {
    return 'bg-border-primary';
  }

  if (value <= HEALTH_DISPLAY.RISKY_MAX) {
    return 'bg-red-100/50';
  }

  if (value >= HEALTH_DISPLAY.HEALTHY_MIN) {
    return 'bg-green-100/50';
  }

  return 'bg-vault-caution/50';
};

export const shouldShowStripes = (isLoading: boolean, value?: number): boolean => {
  if (isLoading) {
    return false;
  }

  if ((value !== undefined && getIsInfinity(value)) || Number.isNaN(value)) {
    return false;
  }

  return true;
};

export const getColorHex = (isLoading: boolean, value: number, unitInVault: number, btcInVault: number): string => {
  value = getDisplayedHealthPercent(value);

  if (isLoading) {
    return '#28272c';
  }

  if ((getIsInfinity(value) && !getIsFullyHealthy(unitInVault, btcInVault)) || Number.isNaN(value)) {
    return '#ddd';
  }

  if (value <= HEALTH_DISPLAY.RISKY_MAX) {
    return '#d04c68';
  }

  if (value >= HEALTH_DISPLAY.HEALTHY_MIN) {
    return '#59aa8a';
  }

  return '#fde37b';
};

export const getIndicatorBackgroundColor = (
  isLoading: boolean,
  isStriped: boolean,
  value: number,
  unitInVault: number,
  btcInVault: number,
): string => {
  if (getIsInfinity(value) && !getIsFullyHealthy(unitInVault, btcInVault)) {
    return 'bg-text-primary';
  }

  if (isStriped) {
    return getBackgroundColorClass(isLoading, value);
  }

  return getProgressBarColorClass(isLoading, value, unitInVault, btcInVault);
};

export const getProgressBarHealthValue = (healthValue: number): number => {
  if (healthValue < HEALTH_DISPLAY.RISKY_MIN) {
    return HEALTH_DISPLAY.RISKY_MIN;
  }

  if (healthValue > HEALTH_DISPLAY.HEALTHY_MAX) {
    return HEALTH_DISPLAY.HEALTHY_MAX;
  }

  return healthValue;
};

export const getStatusRowHealthFactor = (value: string | number) => {
  if (typeof value !== 'number') {
    return null;
  }

  if (getIsInfinity(value)) {
    return null;
  }

  return getHealthFactor(value);
};

export const getAmountTextColorClass = (value: number, unitInVault: number, btcInVault: number): string => {
  if ((getIsInfinity(value) && !getIsFullyHealthy(unitInVault, btcInVault)) || Number.isNaN(value)) {
    return 'text-text-secondary';
  }

  return 'text-text-primary';
};

export const formatAmount = (formattedValue: string, unit: string): string => {
  return `${formattedValue} ${unit}`;
};

export const getVaultDataForOverlay = (hasVault: boolean, isLiquidated: boolean, vaultData: VaultData): VaultData => {
  if (hasVault && isLiquidated) {
    return vaultData;
  }

  return mockData;
};
